/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import {
    object, func, string, bool, arrayOf, shape,
} from 'prop-types';
import mbpLogger from 'mbp-logger';

// Material UI
import {
    Dialog,
    DialogTitle,
    IconButton,
    Grid,
    CircularProgress,
    FormControl,
    RadioGroup,
    FormControlLabel, DialogContent, DialogContentText,
    Radio, FormHelperText, Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

// Helpers
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { enterpriseCSS } from '../../../../style/variables';
import { isEmpty } from '../../../../utils/object';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import customBreakpoints from '../../../../../../helpers/customBreakpoints';
import { formatTextBreakToSpace } from '../../../../utils/formater';
import { secureProtocol } from '../../../../utils/secureProtocol';
import useFetchAPI from '../../../../common/customHooks/useFetchAPI';
import cancelOrderAPI from '../../../../../../../apis/order-apis/cancelOrder';
import { getChannelUser } from '../../../../state/reducers/Helper';
import getSiteConfig from '../../../../utils/getSiteConfig';
import { getFeatureFlags } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getAccessTokenSafely } from '../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';

// Components
import ModalBodyWrapper from './WrapperComponents/ModalBodyWrapper';

const styles = (theme) => ({
    modalTitle: {
        fontSize: '20px',
        textAlign: 'center',
        margin: '14px',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            fontSize: '16px',
        },
    },
    modalTitleContainer: {
        backgroundColor: theme.palette.colorNeutral40,
        padding: 0,
    },
    buttonsWrapper: {
        margin: '30px',
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            margin: '25px 0',
            justifyContent: 'space-evenly',
        },
    },
    closeButton: {
        position: 'absolute',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            top: '0.6rem',
        },
        top: '0.8rem',
        right: '10px',
        color: theme.palette.colorPrimaryAccountCtaButton,
    },
    cancelButton: {
        padding: '10px 0px',
        margin: '0 10px 10px 20px',
        background: theme.palette.common.white,
        cursor: 'pointer',
        color: theme.palette.grey3,
        border: `1px solid ${theme.palette.grey3}`,
        width: '225px',
        fontSize: 14,
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            borderRadius: '50px',
            width: '40%',
            margin: 0,
            fontSize: '12px',
        },
        '&:hover': {
            color: enterpriseCSS.palette.black,
        },
    },
    CTAButton: {
        padding: '10px 0px',
        margin: '0 20px 10px 10px',
        background: theme.palette.colorPrimaryAccountCtaButton,
        color: theme.palette.common.white,
        border: 0,
        cursor: 'pointer',
        fontSize: '16px',
        width: '225px',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            borderRadius: '50px',
            width: '40%',
            margin: 0,
            fontSize: '14px',
        },
        '&:hover': {
            background: theme.palette.colorPrimaryAccountCtaButton,
            opacity: '.70',
        },
    },
    nextButtonCTA: {
        padding: '10px 0px',
        margin: '0 20px 10px 10px',
        background: theme.palette.colorPrimaryAccountCtaButton,
        color: theme.palette.common.white,
        border: 0,
        cursor: 'pointer',
        fontSize: '18px',
        width: '80%',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '80%',
            borderRadius: '50px',
            margin: 0,
            fontSize: '18px',
        },
        '&:hover': {
            background: theme.palette.colorPrimaryAccountCtaButton,
            opacity: '.70',
        },
    },
    disabled: {
        backgroundColor: theme.palette.disableButton,
        color: `${enterpriseCSS.palette.white} !important`,
    },
    modalInnerContent: {
        marginLeft: '20px',
        position: 'relative',
    },
    modalContentContainer: {
        maxHeight: '600px',
        minHeight: '340px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    singleButtonContainer: {
        height: '225px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    singleButtonWrapper: {
        height: '75px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingSpinnerContainer: {
        display: 'block',
        margin: 'auto',
    },
    modalBodyTextContainer: {
        paddingTop: '10px !important',
        overflowY: 'auto',
    },
    itemNotCancellable: {
        color: theme.palette.error.main || '#ff0000',
        marginLeft: '3vw',
        marginTop: '-9px',
        fontSize: '16px',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            marginTop: '4px',
        },
    },
    divider: {
        marginTop: '9px',
        marginBottom: '9px',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
        },
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.colorNeutral40}`,
        },
    },
    fontStyle: {
        color: enterpriseCSS.palette.black,
        fontSize: '16px',
    },
    disabledStyle: {
        color: 'rgba(0, 0, 0, 0.38)',
        fontSize: '16px',
    },
    recipientFontStyle: {
        color: enterpriseCSS.palette.black,
    },
});

const CancelOrderModal = (props) => {
    const { classes } = props;

    const dispatch = useDispatch();

    const [cancelOrderDetails, setCancelOrderDetails] = useState({
        isCancelSubmitted: false,
        reason: '',
        intOrderNumberToCancel: '',
        showCancelReasonPage: false,
        allProductBrands: [],
        hideCloseIcon: false,
        cancelResponse: {},
        cancelResponseError: '',
        confirmBtnClicked: false,
    });

    const ssrDeviceType = useSelector(getSSRDeviceType);
    const featureFlags = useSelector(getFeatureFlags);

    useEffect(() => {
        const { lineItemOrders } = props;
        const allProductBrands = [];
        lineItemOrders.forEach((lineItem) => {
            const firstItemProductBrand = lineItem?.items?.[0]?.productBrand;
            allProductBrands.push(firstItemProductBrand);
        });
        setCancelOrderDetails({
            ...cancelOrderDetails,
            allProductBrands,
        });
    }, []);

    const onSuccess = ({ data, status }) => {
        setCancelOrderDetails({
            ...cancelOrderDetails,
            cancelResponse: { data, status },
        });
    };

    const onError = (err) => {
        setCancelOrderDetails({
            ...cancelOrderDetails,
            cancelResponseError: 'Error while cancelling order',
        });
        mbpLogger.logError({
            function: 'Services.cancelOrder',
            appName: process.env.npm_package_name,
            module: 'Services API Call',
            message: 'Error while cancelling order',
            err,
        });
    };

    const createFetchOrderDetailsMutation = useFetchAPI(onSuccess, onError, cancelOrderAPI, true);

    const handleClickCancel = async () => {
        const { orderNumber, tiEmail } = props;
        const { reason, intOrderNumberToCancel } = cancelOrderDetails;

        const formattedCancelReason = formatTextBreakToSpace(reason);
        const accessToken = await getAccessTokenSafely();
        const isWebUsingNewChannelCode = featureFlags['is-web-using-new-channel-codes'];
        const user = getChannelUser(ssrDeviceType, getSiteConfig('domain'), isWebUsingNewChannelCode);

        setCancelOrderDetails({
            ...cancelOrderDetails,
            isCancelSubmitted: true,
        });
        if (intOrderNumberToCancel === 'all') {
            dispatch(trackEvent({
                eventCategory: 'Submitting Cancel Request',
                eventAction: 'Cancel Order',
                eventLabel: orderNumber || '',
            }));

            createFetchOrderDetailsMutation.mutate({
                jwt: accessToken,
                data: {
                    user,
                    ti_email: tiEmail,
                    orderNumber,
                    reason: formattedCancelReason,
                },
            });
        }
        if (intOrderNumberToCancel !== '' && intOrderNumberToCancel !== 'all') {
            dispatch(trackEvent({
                eventCategory: 'Submitting Cancel Request',
                eventAction: 'Cancel Order',
                eventLabel: orderNumber || '',
            }));
            createFetchOrderDetailsMutation.mutate({
                jwt: accessToken,
                data: {
                    user,
                    ti_email: tiEmail,
                    orderNumber,
                    intOrderNumber: intOrderNumberToCancel,
                    reason: formattedCancelReason,
                },
            });
        }
        setCancelOrderDetails({
            ...cancelOrderDetails,
            isCancelSubmitted: true,
            hideCloseIcon: true,
        });
    };

    useEffect(() => {
        if (cancelOrderDetails.intOrderNumberToCancel === 'all' && cancelOrderDetails.confirmBtnClicked) {
            handleClickCancel();
        }
    }, [cancelOrderDetails.intOrderNumberToCancel, cancelOrderDetails.confirmBtnClicked]);

    const renderButton = (buttonFunction, buttonText, customClassName) => {
        const { handleChangeModalState } = props;
        const { isCancelSubmitted, intOrderNumberToCancel, reason } = cancelOrderDetails;
        const disableButtonAfterSubmission = (buttonFunction !== 'close' && isCancelSubmitted)
            || (buttonFunction === 'nextPage' && intOrderNumberToCancel === '')
            || (buttonFunction === 'proceed-cancel' && reason.trim() === '');

        const buttonStyles = () => {
            if (customClassName) return classes[customClassName];
            if (buttonText === 'Exit' || buttonText === 'Go Back') {
                return classes.cancelButton;
            }
            return classes.CTAButton;
        };

        return (
            <Button
                className={
                    buttonStyles()
                }
                classes={{
                    disabled: classes.disabled,
                }}
                onClick={() => {
                    if (buttonFunction === 'back') {
                        setCancelOrderDetails({
                            ...cancelOrderDetails,
                            showCancelReasonPage: false,
                        });
                    } else if (buttonFunction === 'close') {
                        handleChangeModalState('closeWindow');
                    } else if (buttonFunction === 'nextPage') {
                        setCancelOrderDetails({
                            ...cancelOrderDetails,
                            showCancelReasonPage: true,
                        });
                    } else if (intOrderNumberToCancel === '' && buttonText === 'Confirm') {
                        setCancelOrderDetails({
                            ...cancelOrderDetails,
                            intOrderNumberToCancel: 'all',
                            confirmBtnClicked: true,
                        });
                    } else {
                        setCancelOrderDetails({
                            ...cancelOrderDetails,
                            showCancelReasonPage: false,
                        });
                        handleClickCancel();
                    }
                }}
                disabled={disableButtonAfterSubmission}
            >
                {buttonText}
            </Button>
        );
    };

    const isLineItemCancelEnabledForBrands = () => {
        const { lineItemCancelFeatureFlag } = props;
        const { allProductBrands } = cancelOrderDetails;
        let shouldRenderProductSelectionPage = false;
        allProductBrands.forEach((productBrand) => {
            if (lineItemCancelFeatureFlag[productBrand]) {
                shouldRenderProductSelectionPage = true;
            }
        });
        return shouldRenderProductSelectionPage;
    };

    const renderModalHeader = () => {
        const { handleChangeModalState, lineItemOrders } = props;
        const {
            showCancelReasonPage, isCancelSubmitted, hideCloseIcon, cancelResponse,
        } = cancelOrderDetails;
        let productSelectionPageTitle = 'CANCEL ORDER';
        if (lineItemOrders?.length > 1 && !isCancelSubmitted && !showCancelReasonPage && isLineItemCancelEnabledForBrands()) {
            productSelectionPageTitle = 'SELECT ITEM TO CANCEL';
        } else if (!isEmpty(cancelResponse) && isCancelSubmitted) {
            const { status, data } = cancelResponse;
            if ((status === 200 && data?.message === 'Valid Message') || (status === 200 && data?.message === 'Whole order is cancelled')) {
                productSelectionPageTitle = 'CONFIRM CANCELLATION';
            }
        }

        return (
            <DialogTitle className={classes.modalTitleContainer}>
                <Grid container justifyContent="center" direction="flex-end">
                    <Grid item>
                        <div className={classes.modalTitle}>
                            {productSelectionPageTitle}
                            {!hideCloseIcon
                                && (
                                    <IconButton
                                        aria-label="close-modal"
                                        className={classes.closeButton}
                                        size="small"
                                        onClick={() => handleChangeModalState('closeWindow')}
                                        edge="end"
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                        </div>
                    </Grid>
                </Grid>
            </DialogTitle>
        );
    };

    const renderProductSelectionPage = () => {
        const { lineItemOrders = [], wholeOrderCancelFlag, lineItemCancelFeatureFlag } = props;
        const { intOrderNumberToCancel } = cancelOrderDetails;

        const getLogicalOrder = (lineItem = {}) => {
            const {
                recipientProfiles, intOrderNumber, items, pendingCancel, cancelFlag, status,
            } = lineItem;
            const { firstName, lastName } = recipientProfiles?.[0];
            const isLineItemCancelFlagOn = lineItemCancelFeatureFlag[items?.[0]?.productBrand];
            const showItemNotCancallable = cancelFlag !== 'Y' || pendingCancel || !isLineItemCancelFlagOn;
            let itemNotCancellableText = '';
            if (pendingCancel && status === 'CONFIRMED') {
                itemNotCancellableText = 'This item is pending cancellation.';
            } else if (status === 'CANCELLED') {
                itemNotCancellableText = 'This item is already cancelled.';
            } else if (cancelFlag !== 'Y') {
                itemNotCancellableText = 'This item cannot be cancelled.';
            } else if (cancelFlag === 'Y' && !isLineItemCancelFlagOn) {
                itemNotCancellableText = 'This item may not be cancelled individually.';
            }

            const fontClass = showItemNotCancallable ? classes.disabledStyle : classes.fontStyle;

            return (
                <>
                    <FormControlLabel
                        value={intOrderNumber}
                        control={<Radio color="primary" />}
                        disabled={showItemNotCancallable}
                        label={`${firstName} ${lastName}`}
                        className={classes.recipientFontStyle}
                    />
                    {
                        items.map((item) => (
                            <Grid container xs={12} style={{ marginLeft: '3vw' }}>
                                <Grid item xs={3}>
                                    <img src={secureProtocol(item.prdImgTmn)} height="80%" width="80%" alt="product-image" />
                                </Grid>
                                <Grid item xs={9}>
                                    <Grid item xs={12}>
                                        <FormHelperText className={fontClass}>
                                            {item.productName}
                                        </FormHelperText>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormHelperText className={fontClass}>
                                            ${item.price}
                                        </FormHelperText>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))
                    }
                    {showItemNotCancallable && <FormHelperText className={classes.itemNotCancellable}>{itemNotCancellableText}</FormHelperText>}
                    <div className={classes.divider} />
                </>
            );
        };

        const isEligibleForWholeOrderCancel = () => {
            const isMoreThanOneOrder = lineItemOrders?.length > 1;
            let isOrderPartiallyNotCancelled = true;
            const isLineItemCancelOffForAnyBrands = false;
            lineItemOrders?.forEach((lineItem) => {
                if (
                    lineItem?.status === 'CANCELLED'
                    || lineItem?.pendingCancel === true
                ) {
                    isOrderPartiallyNotCancelled = false;
                }
            });
            return isMoreThanOneOrder && isOrderPartiallyNotCancelled && wholeOrderCancelFlag && !isLineItemCancelOffForAnyBrands;
        };

        return (
            <div className={classes.modalContentContainer}>
                <DialogContent>
                    <DialogContentText>
                        <FormControl>
                            <RadioGroup
                                defaultValue={intOrderNumberToCancel} // to show selected item selected when user came back to product selection page
                                onChange={(e) => {
                                    setCancelOrderDetails({
                                        ...cancelOrderDetails,
                                        intOrderNumberToCancel: e.target.value,
                                    });
                                }}
                            >
                                {lineItemOrders.map((lineItem) => (getLogicalOrder(lineItem)))}
                                {
                                    isEligibleForWholeOrderCancel() && (
                                        <FormControlLabel value="all" control={<Radio color="primary" />} label="Cancel all items" className={classes.recipientFontStyle} />
                                    )
                                }
                            </RadioGroup>
                        </FormControl>
                        <div className={classes.buttonsWrapper}>
                            {renderButton('nextPage', 'Next', 'nextButtonCTA')}
                        </div>
                    </DialogContentText>
                </DialogContent>
            </div>
        );
    };

    const renderLandingPage = () => {
        const {
            isCancelReasonEnabled, lineItemOrders,
            lineItemCancelFeatureFlag, wholeOrderCancelFlag, wholeOrderCancelFeatureFlag,
        } = props;
        const { intOrderNumberToCancel } = cancelOrderDetails;
        const deviceType = ssrDeviceType === 'mobile' ? 'mobile' : 'desktop';
        const isMobile = deviceType === 'mobile';
        const isWholeOrderCancelEnabled = wholeOrderCancelFeatureFlag && wholeOrderCancelFlag;
        const showCloseButton = lineItemOrders?.length === 1 || (!Object.values(lineItemCancelFeatureFlag).includes(true) && isWholeOrderCancelEnabled);

        const getCancelReason = (cancelReasonText) => {
            setCancelOrderDetails({
                ...cancelOrderDetails,
                reason: cancelReasonText,
            });
        };

        const getSelectedItemSummary = () => {
            // if there is only one line item
            if (lineItemOrders.length === 1) {
                const firstName = lineItemOrders?.[0]?.recipientProfiles?.[0]?.firstName;
                const lastName = lineItemOrders?.[0]?.recipientProfiles?.[0]?.lastName;
                const items = lineItemOrders?.[0]?.items;
                return {
                    firstName,
                    lastName,
                    items,
                };
            }

            // if there is more than one line item
            if (intOrderNumberToCancel !== 'all') {
                const selectedItemIndex = lineItemOrders?.findIndex((lineitem) => lineitem?.intOrderNumber === intOrderNumberToCancel);
                const selectedItem = lineItemOrders?.[selectedItemIndex];
                const firstName = selectedItem?.recipientProfiles?.[0]?.firstName;
                const lastName = selectedItem?.recipientProfiles?.[0]?.lastName;
                return {
                    firstName,
                    lastName,
                    items: selectedItem?.items,
                };
            }
            return {};
        };

        const bodyText = {
            cancelReasonTitle: 'Please let us know why you are canceling:',
        };

        return (
            <div className={classes.modalContentContainer}>
                <ModalBodyWrapper
                    bodyText={bodyText}
                    isMobile={isMobile}
                    getCancelReason={getCancelReason}
                    isCancelReasonEnabled={isCancelReasonEnabled}
                    intOrderNumberToCancel={intOrderNumberToCancel}
                    getSelectedItemSummary={getSelectedItemSummary}
                    //   classes={classes}
                />
                <div className={classes.buttonsWrapper}>
                    {!showCloseButton && renderButton('back', 'Go Back')}
                    {!showCloseButton ? renderButton('proceed-cancel', 'Confirm') : renderButton('proceed-cancel', 'Confirm', 'nextButtonCTA')}
                </div>
            </div>
        );
    };

    const renderCancelConfirmationPage = (refundAmount) => {
        const bodyText = {};
        if (props.isCancelRefundAmountDisplayed) {
            bodyText.line1 = 'Your order has been successfully canceled.';
            bodyText.line2 = ReactHtmlParser(`Your refund amount of $${refundAmount} will be processed and applied to your original payment method in 1 to 2 billing cycles.`);
            bodyText.line3 = 'It may take up to 15 minutes for your order history to reflect these changes.';
        } else {
            bodyText.line1 = 'Your order has been successfully canceled.';
            bodyText.line2 = 'It may take up to 15 minutes for your order history to reflect these changes.';
        }
        return (
            <div className={classes.modalContentContainer}>
                <ModalBodyWrapper
                    bodyText={bodyText}
                />
                <div className={classes.singleButtonWrapper}>
                    {renderButton('close', 'Close Window')}
                </div>
            </div>
        );
    };

    const renderFallbackUI = () => {
        const bodyText = {
            line1: 'Sorry, maintenance is in-progress.',
            line2: 'For immediate assistance, please click the CHAT icon.',
        };
        return (
            <div className={classes.modalContentContainer}>
                <ModalBodyWrapper
                    bodyText={bodyText}
                />
                <div className={classes.singleButtonWrapper}>
                    {renderButton('close', 'Close Window')}
                </div>
            </div>
        );
    };

    const renderSpinner = () => (
        <div className={classes.modalContentContainer}>
            <div className={classes.loadingSpinnerContainer}>
                <CircularProgress color="inherit" />
            </div>
        </div>
    );

    const renderModalBody = () => {
        const {
            lineItemOrders,
        } = props;
        const {
            isCancelSubmitted, showCancelReasonPage, cancelResponse, cancelResponseError,
        } = cancelOrderDetails;
        // only render product selection page if there is more than one line item to choose from

        if (
            lineItemOrders?.length > 1
            && !isCancelSubmitted
            && !showCancelReasonPage
            // if there is at least one brand in this order eligible for line item cancel, show product selection page
            && isLineItemCancelEnabledForBrands()
        ) {
            return renderProductSelectionPage();
        }

        if (!isCancelSubmitted) {
            if (
                showCancelReasonPage
                || lineItemOrders?.length === 1
                || !isLineItemCancelEnabledForBrands()
            ) {
                return renderLandingPage();
            }
        }

        // whole order cancel flow goes through BillingDetails
        // order item cancel flow goes through ProductDetails
        if (!isEmpty(cancelResponse)) {
            const { status, data } = cancelResponse;
            if (
                (status === 200 && data?.message === 'Valid Message')
                || (status === 200 && data?.message === 'Whole order is cancelled')
            ) {
                return renderCancelConfirmationPage(Number(data?.totalCharge).toFixed(2));
            }
            return renderFallbackUI();
            // cancel by whole order
        }
        if (!isEmpty(cancelResponseError)) {
            return renderFallbackUI();
        }
        return renderSpinner();
    };

    return (
        <Dialog fullWidth open maxWidth="sm" name="cancel-modal" className={classes.cancelModal}>
            {renderModalHeader()}
            {renderModalBody()}
        </Dialog>
    );
};

CancelOrderModal.propTypes = {
    lineItemOrders: arrayOf(shape({
        items: arrayOf(shape({
            productBrand: bool.isRequired,
        })),
        cancelFlag: bool.isRequired,
    })).isRequired,
    wholeOrderCancelFlag: bool.isRequired,
    lineItemCancelFeatureFlag: bool.isRequired,
    classes: object.isRequired,
    handleChangeModalState: func.isRequired,
    orderNumber: string.isRequired,
    isCancelReasonEnabled: bool.isRequired,
    isCancelRefundAmountDisplayed: bool.isRequired,
    tiEmail: string.isRequired,
    wholeOrderCancelFeatureFlag: bool.isRequired,
};

export default withStyles(styles)(CancelOrderModal);
